import get from 'lodash/get';

// First Party Imports
import {toast, toasterActions} from '@homeaway/react-toaster';

// Relative Imports
import listingActions from '../actions/listingActions';
import listingExtendedActions from '../actions/listingExtendedActions';
import EdapActions from '../actions/edapActions';
import {writeCookie, TRIP_COOKIE} from '../utils/cookieHelpers';
import {OpenModalStates} from '../records/listingPageRecord';
import {log} from '../api/logger';
import {fetchGeoDistance} from '../api/geoDistance';
import {logPriceSummaryEvent as logPriceSummaryEventUtil} from './utils/logPriceSummaryEvent';
import {scrollToNavTab} from '../utils/scrollToNavTab';
import {getLocalizedPropertyMetadata} from '../api/propertyMetadata';
import listingPageActions from '../actions/listingPageActions';
import {writeSessionStorage} from '../utils/sessionStorage';
import vtHelpfulnessActions from '../actions/vtHelpfulnessActions';
import {getLoginUrl, getInquiryLoginUrl} from '../utils/universalLogin';

/* eslint-disable complexity */
function setModalOpenStatus(store, isOpen, modalState) {
    const currentState = store.getState().listingPageReducer.openModalState;
    let newState = currentState;
    if (!isOpen && currentState === modalState) {
        newState = OpenModalStates.NONE;
    } else if (isOpen) {
        newState = modalState;
    }
    store.dispatch(listingActions.updateModalState(newState));

    // React-modal sucks... so clean up left over classes and data.
    // This is a ~hack~, and will be deleted once the non-modal stuff is in.
    // Because of that, I am adding a bunch typeof checks.
    /* istanbul ignore next */
    if (newState === OpenModalStates.NONE) {
        if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
            if (window.document.body && window.document.body.dataset) {
                window.document.body.dataset.reactModals = JSON.stringify({});
            }
        }
    }
}
/* eslint-enable complexity */

function setRequestToBookModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;
    setModalOpenStatus(store, isOpen, OpenModalStates.REQUEST_TO_BOOK);
}

function processServerSideEvaluatedTests(store) {
    const buckettedInTests = store.getState().systemReducer.serverSideTestBuckets;

    buckettedInTests.forEach((testName) => {
        store.dispatch(EdapActions.logTestExposure(testName));
    });
}

function setInquiryModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    if (!isOpen) {
        store.dispatch(listingActions.resetInquiryState());
    }
    setModalOpenStatus(store, isOpen, OpenModalStates.INQUIRY);
}

function setReportListingModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.REPORT_LISTING);
}

function setCivilCodeDisclaimerModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.CIVIL_CODE_DISCLAIMER);
}

function setDescriptionModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.DESCRIPTION);
}

function setUnitDescriptionModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.UNIT_DESCRIPTION);
}

function setAmenityModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.AMENITY);
}

function setCancellationTimelineModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.CANCELLATION_TIMELINE);
}

function setOwnerProfileModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.OWNER_PROFILE);
}

function setReviewsModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;
    setModalOpenStatus(store, isOpen, OpenModalStates.REVIEWS);
}

function setSearchModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.SEARCH);
}

function setSearchDatePickerModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.SEARCH_DATE_PICKER);
}

function setSearchGuestPickerModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.SEARCH_GUEST_PICKER);
}

function setVirtualTourModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.VIRTUAL_TOUR);
}

function setVerticalGalleryModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;
    setModalOpenStatus(store, isOpen, OpenModalStates.VERTICAL_GALLERY);
}

function setImageCarouselModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;
    setModalOpenStatus(store, isOpen, OpenModalStates.IMAGE_CAROUSEL);
}

function setToiModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;
    setModalOpenStatus(store, isOpen, OpenModalStates.TOI_MAP);
}

function updateTripDataCookie(store) {
    const tripData = store.getState().tripDetailsReducer;

    writeCookie(TRIP_COOKIE, {
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        numberOfAdults: tripData.adults,
        numberOfChildren: tripData.children,
        petIncluded: tripData.hasPets
    });
}

function viewPriceDetailsClicked(store, action = {payload: {}}) {
    const {actionLocation} = action.payload;
    const clickedFromStickyBookingPanel = actionLocation === 'sticky booking panel';

    store.dispatch(
        EdapActions.triggerEdapEvent('generic.event', {
            eventcategory: 'pdp page',
            eventaction: clickedFromStickyBookingPanel
                ? 'sticky booking panel view details link click'
                : 'detailed price link click'
        })
    );

    store.dispatch(listingActions.setRequestToBookModalOpen(true));
}

function inquiryClicked(store, action = {payload: {}}) {
    const {travelerFeaturesReducer} = store.getState();
    const loggedIn = travelerFeaturesReducer.isLoggedIn;
    const {actionLocation} = action.payload;

    if (!loggedIn) {
        const universalLoginUrl = getInquiryLoginUrl(window.location.href);
        window.location.assign(universalLoginUrl);
        return;
    }

    store.dispatch(listingActions.setInquiryModalOpen(true));

    store.dispatch(
        EdapActions.triggerEdapEvent('generic.modal', {
            modalname: 'inquiry',
            modalaction: 'open',
            actionlocation: actionLocation
        })
    );
}

function reportListingClicked(store = {payload: {}}) {
    const {travelerFeaturesReducer} = store.getState();
    const {isLoggedIn} = travelerFeaturesReducer;
    if (!isLoggedIn) {
        const universalLoginUrl = getLoginUrl(window.location.href, OpenModalStates.REPORT_LISTING);
        window.location.assign(universalLoginUrl);

        return;
    }

    store.dispatch(listingActions.setReportListingModalOpen(true));
}

function civilCodeDisclaimerClicked(store, action = {payload: {}}) {
    const {actionLocation} = action.payload;

    store.dispatch(listingActions.setCivilCodeDisclaimerModalOpen(true));

    store.dispatch(
        EdapActions.triggerEdapEvent('generic.modal', {
            modalname: 'civil code disclaimer',
            modalaction: 'open',
            actionlocation: actionLocation
        })
    );
}

function navClicked(store, action = {payload: {}}) {
    const {tabName, largeNav} = action.payload;

    store.dispatch(
        EdapActions.triggerEdapEvent('listing.infotab.click', {
            tabname: tabName
        })
    );
    store.dispatch(EdapActions.triggerEdapUxPropertyDetailsNavigationSelected(tabName));
    scrollToNavTab(tabName, 500, 0, largeNav ? 12 : 2);
}

function logPriceSummaryEvent(store, action = {payload: {}}) {
    logPriceSummaryEventUtil(store, action.payload.priceSummaryEventParams);
}

function triggerUrgencyToast(store, action = {payload: {}}) {
    const {element} = action.payload;

    const urgencyToast = toast(element, {
        type: 'inverse'
    });

    store.dispatch(toasterActions.addToast(urgencyToast));
}

function handleDescriptionDisclaimerVisibility(
    store,
    descriptionDisclaimerVisibilityControl,
    alertVisibilityControl
) {
    store.dispatch(
        listingPageActions.showPartnerDescriptionDisclaimer(descriptionDisclaimerVisibilityControl)
    );
    store.dispatch(listingPageActions.localizeDescriptionFailure(alertVisibilityControl));
}

function handleFetchLocalizedDescriptionSuccess(
    store,
    localizedDescription,
    descriptionDisclaimerVisibilityControl,
    alertVisibilityControl
) {
    store.dispatch(listingActions.translateDescriptionSuccess(localizedDescription));
    handleDescriptionDisclaimerVisibility(
        store,
        descriptionDisclaimerVisibilityControl,
        alertVisibilityControl
    );
}

function fetchPropertyDescriptionLocalized(store, action = {payload: {}}) {
    const {locale} = action.payload;
    const {listingReducer, listingExtendedReducer} = store.getState();

    if (
        !listingReducer.localizedPropertyDescriptionMap[locale] &&
        !(
            listingExtendedReducer.localizedPropertyDescriptionMap &&
            listingExtendedReducer.localizedPropertyDescriptionMap[locale]
        )
    ) {
        return getLocalizedPropertyMetadata({
            listingId: listingReducer.listingId,
            locale
        })
            .then((response) => {
                if (response && response.ok && response.body.localizedDescription.value) {
                    const {localizedDescription} = response.body;
                    handleFetchLocalizedDescriptionSuccess(
                        store,
                        localizedDescription.value,
                        false,
                        false
                    );
                    store.dispatch(
                        listingActions.savePropertyDescription(localizedDescription.value, locale)
                    );
                    return;
                }
                store.dispatch(listingPageActions.localizeDescriptionFailure(true));
                return;
            })
            .catch((error) => {
                handleDescriptionDisclaimerVisibility(store, true, true);
                log({
                    localizedDescription: null,
                    error: `fetchPropertyDescriptionLocalized exception.  Error Message: ${
                        error.message
                    }`
                });
            });
    }
    const localizedDescription =
        listingReducer.localizedPropertyDescriptionMap[locale] ||
        listingExtendedReducer.localizedPropertyDescriptionMap[locale];
    handleFetchLocalizedDescriptionSuccess(store, localizedDescription, false, false);
    return localizedDescription;
}

function pageLoadedFetchGeoDistance(store) {
    const state = store.getState();

    const params = {
        listingId: get(state, 'listingReducer.listingId'),
        locale: get(state, 'systemReducer.locale')
    };

    return fetchGeoDistance(params)
        .then((response) => {
            if (!response.body) {
                return;
            }

            store.dispatch(listingExtendedActions.fetchGeoDistanceSuccess(response.body));
        })
        .catch((error) => {
            log({
                fetchGeoDistance: false,
                error: `fetchGeoDistance exception.  Error Message: ${error.message}`
            });
        });
}

function triggerVTExitToast(store, action = {payload: {}}) {
    const {viewerVersion, helpfulnessHistory} = action.payload;
    const historyUpdateValue = {[`${viewerVersion}ToastPresented`]: true};
    const newHistory = Object.assign(helpfulnessHistory, historyUpdateValue);
    writeSessionStorage('vt-helpfulness-history', newHistory);
    store.dispatch(vtHelpfulnessActions.updateVTHelpfulnessHistory(newHistory));
}

export default setRequestToBookModalOpen;
export {
    civilCodeDisclaimerClicked,
    setAmenityModalOpen,
    setCancellationTimelineModalOpen,
    setCivilCodeDisclaimerModalOpen,
    setDescriptionModalOpen,
    setImageCarouselModalOpen,
    setInquiryModalOpen,
    setReportListingModalOpen,
    setOwnerProfileModalOpen,
    setRequestToBookModalOpen,
    setReviewsModalOpen,
    setSearchDatePickerModalOpen,
    setSearchGuestPickerModalOpen,
    setSearchModalOpen,
    setUnitDescriptionModalOpen,
    setVerticalGalleryModalOpen,
    setToiModalOpen,
    pageLoadedFetchGeoDistance,
    processServerSideEvaluatedTests,
    updateTripDataCookie,
    viewPriceDetailsClicked,
    inquiryClicked,
    navClicked,
    logPriceSummaryEvent,
    reportListingClicked,
    setVirtualTourModalOpen,
    triggerUrgencyToast,
    fetchPropertyDescriptionLocalized,
    triggerVTExitToast
};
